import {
  Button,
  Flex,
  IconButton,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { useMemo } from 'react';
import { FaRegTrashCan } from 'react-icons/fa6';
import { GrEdit } from 'react-icons/gr';
import { MdOutlineDiscount } from 'react-icons/md';
import { R$ } from 'shared/helpers/currency';
import { useCreateContractModalContext } from '../modals/CreateContractModal/ModalContext';

interface AddedServicesListProps {
  showDiscount?: boolean;
  showRemove?: boolean;
  showEdit?: boolean;
  onEditService?: (serviceId: number) => void;
  variant?: 'simple' | 'stripped' | 'unstyled';
}

const AddedServicesList: React.FC<AddedServicesListProps> = ({
  showDiscount = false,
  showRemove = false,
  showEdit = false,
  onEditService,
  variant = 'simple',
}) => {
  const { addedProducts, removePlan, setDiscountProduct } =
    useCreateContractModalContext();

  const toNumber = (v: unknown): number => {
    if (typeof v === 'number') return v;
    if (typeof v === 'string') {
      const normalized = v
        .replace(/\s/g, '')
        .replace(/[R$\u00A0]/g, '')
        .replace(/\./g, '')
        .replace(',', '.');
      const n = parseFloat(normalized);
      return Number.isFinite(n) ? n : 0;
    }
    return 0;
  };

  const total = useMemo(() => {
    return addedProducts.reduce((sum, product) => {
      if (product.discount?.product_value_with_discount != null) {
        return sum + toNumber(product.discount.product_value_with_discount);
      }
      const serviceValue = product.selectedService?.value;
      return sum + (typeof serviceValue === 'number' ? serviceValue : 0);
    }, 0);
  }, [addedProducts]);

  const hasItems = addedProducts.length > 0;

  return (
    <Stack>
      {hasItems && (
        <>
          <TableContainer px={2}>
            <Table
              size="sm"
              variant={variant}
              sx={{ 'th, td': { fontSize: '1rem !important' } }}
            >
              <Thead backgroundColor={'#F5F6F6'}>
                <Tr>
                  <Th textAlign={'left'}>Nome</Th>
                  <Th textAlign={'left'}>Categoria</Th>
                  <Th textAlign={'left'}>Valor</Th>
                  {showDiscount && <Th textAlign={'left'}>Desconto</Th>}
                  {showRemove && <Th></Th>}
                  {showEdit && <Th>Editar</Th>}
                </Tr>
              </Thead>
              <Tbody>
                {addedProducts.map((product) => (
                  <Tr key={product?.selectedService?.id}>
                    <Td>{product.selectedService?.name}</Td>
                    <Td>
                      {product.selectedService?.is_license === 1
                        ? 'Plano'
                        : 'Serviço'}
                    </Td>
                    <Td textAlign={'left'}>
                      {product.discount ? (
                        <Stack>
                          <Text fontSize="1rem" as="s" textColor={'red.600'}>
                            {R$(product.discount.product_total_value)}
                          </Text>
                          <Text fontSize="1rem" textColor={'green.600'}>
                            {R$(product.discount.product_value_with_discount)}
                          </Text>
                        </Stack>
                      ) : (
                        product.selectedService?.priceString
                      )}
                    </Td>
                    {showDiscount && (
                      <Td>
                        <Button
                          variant="ghost"
                          colorScheme="yooga"
                          size="sm"
                          leftIcon={<MdOutlineDiscount />}
                          onClick={() => setDiscountProduct(product)}
                        >
                          Aplicar Desconto
                        </Button>
                      </Td>
                    )}
                    {showRemove && (
                      <Td>
                        <IconButton
                          aria-label="Remover"
                          colorScheme="red"
                          variant="ghost"
                          size="sm"
                          icon={<FaRegTrashCan />}
                          onClick={() =>
                            removePlan(product.selectedService?.id as number)
                          }
                        />
                      </Td>
                    )}
                    {showEdit && (
                      <Td>
                        <IconButton
                          aria-label="Editar"
                          colorScheme="gray"
                          variant="ghost"
                          size="md"
                          icon={<GrEdit />}
                          onClick={() =>
                            product.selectedService?.id &&
                            onEditService?.(product.selectedService.id)
                          }
                        />
                      </Td>
                    )}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
          <Flex justify="right" align="center" px={2}>
            <Text fontWeight="semibold" fontSize="1.25rem">
              Total: {R$(total)}
            </Text>
          </Flex>
        </>
      )}
    </Stack>
  );
};

export default AddedServicesList;
